import { Stack } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import type { UseFormReturnType } from '@mantine/form';

import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { createEnterKeyHandler } from '~/domains/auth/utils/formHelpers';
import dt from '~/testing';

import { ConnectedRegistrationBonusBanner } from '../../RegistrationBonusBanner/ConnectedRegistrationBonusBanner';
import { RecaptchaText } from '../../RecaptchaText';
import PasswordInput from '../../PasswordInput/PasswordInput';
import { passwordRequirements } from '../../PasswordInput/utils';
import type { SignUpFormValues } from '../types';
import { RegistrationPromoCodeBanner } from '../../RegistrationPromoCodeBanner';

import useStyles from './styles';
import { SignUpContinueButton } from './SignUpContinueButton';

interface PasswordFormProps {
  form: UseFormReturnType<SignUpFormValues>;
  onClick: () => void;
}

export function PasswordForm({ form, onClick }: PasswordFormProps) {
  const { t } = useTranslation('auth');
  const { classes, theme } = useStyles();

  const password = form.values.password || '';

  // Check if password meets all requirements
  const isPasswordValid = passwordRequirements.every((requirement) =>
    requirement.re.test(password)
  );

  const handleKeyDown = createEnterKeyHandler(!isPasswordValid, onClick);

  return (
    <>
      <div className={classes.headerContainer}>
        <SignUpHeader
          title={t('signUp.headers.password.title')}
          copy={t('signUp.headers.password.copy')}
        />
      </div>
      <PasswordInput
        passwordProps={form.getInputProps('password')}
        showPopover={false}
        backgroundColor={theme.colors.gray[1]}
        hasBorder={false}
        autoFocus
        onKeyDown={handleKeyDown}
      />

      <Stack spacing={theme.other.spacing._12} mt={theme.other.spacing._40}>
        <RegistrationPromoCodeBanner
          promoModalProps={form.values._promoModalProps}
          referralCode={form.values._referralCodeValue}
          promoCode={form.values._promoCodeValue}
        />
        <SignUpContinueButton
          onClick={onClick}
          disabled={!isPasswordValid}
          data-test-id={dt.components.shared.auth.signUpVersionTwo.passwordForm.continueButton}
        />
        <ConnectedRegistrationBonusBanner />
      </Stack>
      <RecaptchaText textAlign="left" />
    </>
  );
}
